import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Layout from '../../components/Layout'
import Spinner from '../../components/Spinner'
import { getStatsOverview } from '../../api/adminStats'
import { getSettings } from '../../api/settings'
import { formatMoney, formatMoneyHint } from '../../utils/format'
import { useAuth } from '../../context/AuthContext'
import { useT } from '../../i18n'

const G = '#C89B4C'
const TX = '#F5F4F0'
const TX2 = '#A8A8AE'
const TX3 = '#8A8A96'
const GOOD = '#6FB89A'
const BAD = '#D97757'
const WARN = '#F97316'

export default function DashboardPage() {
  const { user } = useAuth()
  const lang = user?.language ?? 'ru'
  const tr = useT(lang)
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['admin-stats-overview'],
    queryFn: getStatsOverview,
    refetchInterval: 60_000,
  })
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings, staleTime: 60_000 })
  const usdRate = settings?.usd_rate ?? 12000

  if (isLoading || !data) {
    return (
      <Layout title={tr('dashTitle')}>
        <Spinner className="mt-10" />
      </Layout>
    )
  }

  const { orders, finance, payments, clients } = data
  const closedCount = orders.by_status.closed ?? 0
  const otherCount = orders.total - orders.active_count - closedCount
  const statusSlices = [
    { label: tr('tabActive'), value: orders.active_count, color: G },
    { label: tr('tabClosed'), value: closedCount, color: GOOD },
    { label: tr('all'), value: Math.max(otherCount, 0), color: TX3 },
  ]

  const trendDays = last30Days().map((d) => orders.trend_30d.find((t) => t.date === d)?.count ?? 0)

  return (
    <Layout title={tr('dashTitle')}>
      {/* ── Hero: сколько должно прийти в этом месяце ── */}
      <div className="card" style={{ textAlign: 'center', padding: '24px 16px' }}>
        <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: TX3, margin: 0 }}>
          {tr('dueThisMonth')}
        </p>
        <p style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 30, fontWeight: 700, color: TX, margin: '8px 0 2px' }}>
          {formatMoney(finance.month_plan)}
        </p>
        <p style={{ fontSize: 12, color: TX3, margin: 0 }}>{formatMoneyHint(finance.month_plan, usdRate)}</p>
        <div style={{ marginTop: 14 }}>
          <ProgressBar pct={finance.month_collected_pct} />
          <p style={{ fontSize: 11, color: TX3, marginTop: 6 }}>
            {finance.month_collected_pct}% {tr('factLabel').toLowerCase()} · {formatMoney(finance.month_collected)}
          </p>
        </div>
      </div>

      {/* ── KPI grid ── */}
      <div className="an-dash-kpi" style={{ marginTop: 12 }}>
        <KpiTile label={tr('tabActive')} value={String(orders.active_count)} onClick={() => navigate('/admin/orders?status=active')} />
        <KpiTile label={tr('portfolio')} value={formatMoney(finance.portfolio_total)} />
        <KpiTile label={tr('avgRate')} value={`${finance.avg_interest_rate}%`} />
        <KpiTile label={tr('overdue')} value={formatMoney(payments.overdue_amount)} accent={payments.overdue_count > 0 ? BAD : undefined} onClick={() => navigate('/admin/payments?status=overdue')} />
        <KpiTile label={tr('paymentsThisMonth')} value={`${payments.month_count} · ${formatMoney(finance.month_plan)}`} onClick={() => navigate('/admin/payments?period=month')} />
      </div>

      {/* ── Quick links ── */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <LinkChip label={`${tr('totalOrders')}: ${orders.total}`} onClick={() => navigate('/admin/orders')} />
        <LinkChip label={`${tr('clients')}: ${clients.total}`} onClick={() => navigate('/admin/users')} />
        <LinkChip label={`${tr('awaitingConfirmation')}: ${payments.awaiting_confirmation}`} onClick={() => navigate('/admin/payments?status=awaiting_confirmation')} accent={payments.awaiting_confirmation > 0 ? WARN : undefined} />
      </div>

      {/* ── Detail section ── */}
      <div className="an-dash-section" style={{ marginTop: 20 }}>
        {/* Статусы заявок */}
        <div className="card">
          <p className="section-title" style={{ marginBottom: 12 }}>{tr('statusBreakdown')}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <Donut slices={statusSlices} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {statusSlices.map((s) => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: TX2 }}>{s.label}</span>
                  <span style={{ fontSize: 12, fontFamily: '"JetBrains Mono", monospace', color: TX, marginLeft: 'auto' }}>{s.value}</span>
                </div>
              ))}
              <p style={{ fontSize: 11, color: TX3, marginTop: 4 }}>{tr('conversion')}: {orders.conversion_pct}%</p>
            </div>
          </div>
        </div>

        {/* План/факт месяца */}
        <div className="card">
          <p className="section-title" style={{ marginBottom: 12 }}>{tr('planLabel')} / {tr('factLabel')}</p>
          <PlanFactBar plan={finance.month_plan} fact={finance.month_collected} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
            <Stat label={tr('planLabel')} value={formatMoney(finance.month_plan)} />
            <Stat label={tr('factLabel')} value={formatMoney(finance.month_collected)} color={GOOD} />
          </div>
          <p style={{ fontSize: 11, color: TX3, marginTop: 10 }}>
            {tr('expectedIncome')}: {formatMoney(finance.expected_interest_income)} · {tr('avgMonthlyPayment')}: {formatMoney(finance.avg_monthly_amount)}
          </p>
        </div>

        {/* Динамика заявок */}
        <div className="card">
          <p className="section-title" style={{ marginBottom: 12 }}>{tr('ordersTrend')} · {tr('last30days')}</p>
          <Sparkline values={trendDays} />
          <p style={{ fontSize: 11, color: TX3, marginTop: 8 }}>{tr('newThisMonth')}: {orders.new_this_month}</p>
        </div>

        {/* Клиенты */}
        <div className="card">
          <p className="section-title" style={{ marginBottom: 12 }}>{tr('clients')}</p>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <Stat label={tr('totalClients')} value={String(clients.total)} />
            <Stat label={tr('newThisMonth')} value={String(clients.new_this_month)} />
            <Stat label={tr('verifiedCount')} value={`${clients.verified}/${clients.total}`} />
            <Stat label={tr('blockedCount')} value={String(clients.blocked)} color={clients.blocked > 0 ? BAD : undefined} />
          </div>
          <p className="section-title" style={{ marginBottom: 8 }}>{tr('tierBreakdown')}</p>
          <TierBars byTier={clients.by_tier} />
        </div>
      </div>

      <button
        onClick={() => navigate('/admin/orders')}
        style={{ display: 'block', width: '100%', textAlign: 'center', marginTop: 16, padding: '10px', background: 'none', border: 'none', color: G, fontSize: 13, fontWeight: 500 }}
      >
        {tr('allOrders')}
      </button>
    </Layout>
  )
}

function last30Days(): string[] {
  const out: string[] = []
  const d = new Date()
  d.setUTCHours(0, 0, 0, 0)
  for (let i = 29; i >= 0; i--) {
    const day = new Date(d)
    day.setUTCDate(d.getUTCDate() - i)
    out.push(day.toISOString().slice(0, 10))
  }
  return out
}

function KpiTile({ label, value, accent, onClick }: { label: string; value: string; accent?: string; onClick?: () => void }) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag className="card" onClick={onClick} style={{ textAlign: 'left', cursor: onClick ? 'pointer' : 'default' }}>
      <p style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em', color: TX3, margin: 0 }}>{label}</p>
      <p style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 17, fontWeight: 600, color: accent ?? TX, margin: '6px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</p>
    </Tag>
  )
}

function LinkChip({ label, onClick, accent }: { label: string; onClick: () => void; accent?: string }) {
  return (
    <button
      onClick={onClick}
      className="glass-tile"
      style={{ padding: '7px 12px', borderRadius: 99, fontSize: 12, color: accent ?? TX2, fontWeight: 500 }}
    >
      {label}
    </button>
  )
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <p style={{ fontSize: 9, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em', color: TX3, margin: 0 }}>{label}</p>
      <p style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 13, color: color ?? TX, margin: '4px 0 0' }}>{value}</p>
    </div>
  )
}

function ProgressBar({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, pct))
  return (
    <div style={{ height: 8, borderRadius: 99, background: 'rgba(244,238,225,0.10)', overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${clamped}%`, background: `linear-gradient(90deg, ${G}, #9C7A33)`, borderRadius: 99 }} />
    </div>
  )
}

function PlanFactBar({ plan, fact }: { plan: number; fact: number }) {
  const pct = plan > 0 ? Math.min(100, (fact / plan) * 100) : 0
  return (
    <div style={{ height: 14, borderRadius: 6, background: 'rgba(244,238,225,0.10)', overflow: 'hidden', position: 'relative' }}>
      <div style={{ height: '100%', width: `${pct}%`, background: GOOD, borderRadius: 6 }} />
    </div>
  )
}

function Donut({ slices }: { slices: { label: string; value: number; color: string }[] }) {
  const total = slices.reduce((s, x) => s + x.value, 0) || 1
  const R = 30
  const C = 2 * Math.PI * R
  let offset = 0
  return (
    <svg width="76" height="76" viewBox="0 0 76 76" style={{ flexShrink: 0, transform: 'rotate(-90deg)' }}>
      <circle cx="38" cy="38" r={R} fill="none" stroke="rgba(244,238,225,0.08)" strokeWidth="10" />
      {slices.filter((s) => s.value > 0).map((s) => {
        const frac = s.value / total
        const len = frac * C
        const dasharray = `${Math.max(len - 2, 0)} ${C - Math.max(len - 2, 0)}`
        const el = (
          <circle
            key={s.label}
            cx="38" cy="38" r={R} fill="none"
            stroke={s.color} strokeWidth="10" strokeLinecap="round"
            strokeDasharray={dasharray} strokeDashoffset={-offset}
          />
        )
        offset += len
        return el
      })}
    </svg>
  )
}

function Sparkline({ values }: { values: number[] }) {
  const max = Math.max(...values, 1)
  const w = 280, h = 48
  const step = w / Math.max(values.length - 1, 1)
  const points = values.map((v, i) => `${i * step},${h - (v / max) * (h - 4) - 2}`).join(' ')
  const areaPoints = `0,${h} ${points} ${w},${h}`
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      <polygon points={areaPoints} fill="rgba(200,155,76,0.12)" />
      <polyline points={points} fill="none" stroke={G} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

function TierBars({ byTier }: { byTier: Record<string, number> }) {
  const max = Math.max(byTier.bronze ?? 0, byTier.silver ?? 0, byTier.gold ?? 0, 1)
  const rows: { key: string; label: string; color: string }[] = [
    { key: 'gold', label: 'Gold', color: '#EAB308' },
    { key: 'silver', label: 'Silver', color: '#A8A8AE' },
    { key: 'bronze', label: 'Bronze', color: '#C8752A' },
  ]
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {rows.map((r) => {
        const val = byTier[r.key] ?? 0
        return (
          <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: TX2, width: 44, flexShrink: 0 }}>{r.label}</span>
            <div style={{ flex: 1, height: 6, borderRadius: 99, background: 'rgba(244,238,225,0.08)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(val / max) * 100}%`, background: r.color, borderRadius: 99 }} />
            </div>
            <span style={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace', color: TX3, width: 24, textAlign: 'right' }}>{val}</span>
          </div>
        )
      })}
    </div>
  )
}
